import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { validator as patientValidator } from "lib/build/forms/common/patient";
import { Schema } from "lib/build/schema";

import { api } from "src/common/utils/api";
import { useAuthState } from "src/features/auth";
import { FormState } from "src/features/forms";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import {
	PatientForm,
	usePatientForm
} from "src/views/patient-form/PatientForm";
import {
	addMaxItems,
	removeInactiveOptions,
	removeSelectedOptions
} from "src/views/patient-form/utils";

const validator = Schema.form().properties({
	version: Schema.string().optional(),
	timestamp: Schema.dateTime().optional(),
	value: patientValidator
});

const messages = defineMessages({
	addPatient: {
		id: "form.add_patient",
		defaultMessage: "Páciens felvétele"
	}
});

interface AddPatientProps {
	socialSecurityNumber: string;
}

export const AddPatient: React.FunctionComponent<AddPatientProps> = props => {
	const formProps = useAddPatientForm(props);
	return (
		<PatientForm
			titleText={<FormattedMessage {...messages.addPatient} />}
			submitText={<FormattedMessage {...messages.addPatient} />}
			{...formProps}
		/>
	);
};

function useAddPatientForm({ socialSecurityNumber }: AddPatientProps): {
	formState: FormState;
	isSubmitted: boolean;
	isLoading: boolean;
	onSubmit(): Promise<void>;
} {
	const navigate = useNavigate();

	const { user } = useAuthState();
	const { org, mod } = useOrgModParams();
	const createGenericError = useCreateGenericError();

	const rawFormState = usePatientForm(validator, {
		value: {
			socialSecurityNumber,
			connectedUsers: user === null ? [] : [user.id]
		}
	});
	const formState = useConnectedUserOptionOptimizations(rawFormState);

	const [{ isSubmitted, isLoading }, setState] = React.useState({
		isSubmitted: false,
		isLoading: false
	});

	const onSubmit = async (): Promise<void> => {
		setState(state => ({ ...state, isSubmitted: true }));
		if (formState.errors.length === 0) {
			setState(state => ({ ...state, isLoading: true }));
			try {
				const { status, data } =
					(await api.post(
						`/patients?organization=${org}&module=${mod}`,
						formState.value
					)) || {};
				if (status === 205) {
					navigate(`/${org}/${mod}/patients/list`);
				} else {
					navigate(
						`/${org}/${mod}/patients/${data.patientId}/dashboard`
					);
				}
			} catch {
				setState(state => ({ ...state, isLoading: false }));
				createGenericError();
			}
		}
	};

	return {
		formState,
		isSubmitted,
		isLoading,
		onSubmit
	};
}

function useConnectedUserOptionOptimizations(formState: FormState): FormState {
	return React.useMemo(() => {
		const { value, messages: messagesA } = formState;
		const messagesB = removeInactiveOptions(messagesA);
		const messagesC = addMaxItems(messagesB);
		const messagesD = removeSelectedOptions(value, messagesC);
		return messagesA === messagesD
			? formState
			: { ...formState, value, messages: messagesD };
	}, [formState]);
}
